/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks = [];

    const project: ProjectConfiguration = feature.project;
    const BUILTIN = feature.getSettingValue('BUILTIN') === '1';
    const NAME = feature.getSettingValue('NAME');
    let PORT = feature.getSettingValue('PORT');
    if (PORT.indexOf('/') === 0) {
        PORT = `{${PORT}}`;
    }
    const DI = feature.getSettingValue('DI');
    const DO = feature.getSettingValue('DO');
    const CLK = feature.getSettingValue('CLK');
    const device = PlatformUtils.getDeviceDefinition(feature.project.device.id);

    // get CS pins from peripherals
    let SPIPeripherals = [];
    for (let i = 0; i < project.features.length; i++) {
        const settings = project.features[i].settings;
        const SPISetting = settings.find(setting => setting.id === 'SPI' && setting.refId === 'spi');
        if (SPISetting) {
            SPIPeripherals.push(project.features[i]);
        }
    }
    SPIPeripherals = SPIPeripherals.sort((a, b) => {
        return a.getSettingValue('ADDRESS') - b.getSettingValue('ADDRESS');
    });

    let csCode = '';
    if (SPIPeripherals.length > 0) {
        const items = [];
        for (let i = 0; i < SPIPeripherals.length; i++) {
            const settings = SPIPeripherals[i].settings;
            const CS = settings.find(s => s.id === 'CS');
            if (CS) {
                const csFeature = project.getFeature(CS.value);
                if (csFeature && SPIPeripherals[i].getSettingValue('SPI') === feature.getID()) {
                    const GPIO = Peripherals.getGPIOPeripheral(csFeature.getSettingValue('PIN'), project.device.id);
                    items.push(`<&${GPIO.peripheralPort} ${GPIO.num} GPIO_ACTIVE_LOW>`);
                }
            }
        }
        if (items.length > 0) {
            csCode = `cs-gpios = ${items.join(', ')};`;
        }
    }

    blocks.push({
        location: 'PRJ_CONF',
        blockType: BlockType.CODE,
        id: 'CONFIG_GPIO=y',
        template: 'CONFIG_GPIO=y',
    });

    // blocks.push({
    //     location: 'PRJ_CONF',
    //     blockType: BlockType.CODE,
    //     id: 'CONFIG_SPI=y',
    //     template: 'CONFIG_SPI=y',
    // });

    let spiNodeCode = '';
    let pinmuxCode = '';
    if (device.soc.indexOf('esp') === 0) {
        // transform spi2 to SPIM2
        const port = PORT.replace('spi', 'SPIM');
        pinmuxCode = `
group1 {
    pinmux = <${port}_MISO_GPIO${DO}>,
            <${port}_SCLK_GPIO${CLK}>;
};
group2 {
    pinmux = <${port}_MOSI_GPIO${DI}>;
    output-low;
};`;
    } else if (device.soc.indexOf('samd') === 0) {
        // atmel
        // C and D are sercom
        let altFunction = 'C';
        if (PORT === 'sercom7') {
            altFunction = 'D';
        }
        // alternat function is D for SPI
        const atmelPort = PORT.toUpperCase();
        let MISOPin = '';
        let MOSIPin = '';
        let SCKPin = '';
        const MISOPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, DO, 'gpio');
        if (MISOPeripheral) {
            MISOPin = MISOPeripheral.peripheralPort.replace('port', 'P').toUpperCase();
            MISOPin += MISOPeripheral.num;
        }
        const MOSIPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, DI, 'gpio');
        if (MOSIPeripheral) {
            MOSIPin = MOSIPeripheral.peripheralPort.replace('port', 'P').toUpperCase();
            MOSIPin += MOSIPeripheral.num;
        }
        const SCKPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, CLK, 'gpio');
        if (SCKPeripheral) {
            SCKPin = SCKPeripheral.peripheralPort.replace('port', 'P').toUpperCase();
            SCKPin += SCKPeripheral.num;
        }
        spiNodeCode = `
dipo = <2>;
dopo = <3>;        
        `;
        pinmuxCode = `
group1 {
    pinmux = <${SCKPin}${altFunction}_${atmelPort}_PAD1>,
            <${MOSIPin}${altFunction}_${atmelPort}_PAD2>,
            <${MISOPin}${altFunction}_${atmelPort}_PAD3>;
};`;
    }

    blocks.push({
        location: 'APP_OVERLAY',
        blockType: BlockType.CODE,
        template: `
&${PORT} {
    status = "okay";
    ${csCode}
    ${spiNodeCode}
    pinctrl-0 = <&${NAME}_default>;
};`,
    });

    blocks.push({
        location: 'APP_OVERLAY_PINCTRL',
        blockType: BlockType.CODE,
        template: `
${NAME}_default: ${NAME}_default {
    ${pinmuxCode}
};`,
    });
    return blocks;
};
